import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { adminApi, Card, Badge, Table, Toolbar, inpCls, btnPrimary, btnGhost, btnDanger, btnSuccess } from './_shared';
import { Search, UserPlus, KeyRound, UserX, UserCheck, Edit, X, Unlock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const ROLE_OPTIONS = ['USER', 'PLATFORM_ADMIN'];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [active, setActive] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [createForm, setCreateForm] = useState({ email: '', firstName: '', lastName: '', company: '', password: '', platformRole: 'USER' });
  const [pwd, setPwd] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (role) params.platform_role = role;
      if (active) params.active = active;
      const res = await adminApi.users(params);
      setUsers(res.users || []);
    } catch { toast.error('Failed to load users'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, role, active]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createUser(createForm);
      toast.success('User created');
      setShowCreate(false);
      setCreateForm({ email: '', firstName: '', lastName: '', company: '', password: '', platformRole: 'USER' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveEdit = async () => {
    try {
      await adminApi.updateUser(editing.user_id, {
        firstName: editing.firstName, lastName: editing.lastName,
        company: editing.company, platformRole: editing.platformRole,
        isActive: editing.isActive,
      });
      toast.success('User updated');
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const reset = async () => {
    if (!pwd || pwd.length < 6) { toast.error('Password ≥ 6 chars'); return; }
    try {
      await adminApi.resetPwd(resetting.user_id, pwd);
      toast.success(`Password reset for ${resetting.email}`);
      setResetting(null); setPwd('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deactivate = async (u) => {
    if (!window.confirm(`Revoke access for ${u.email}? They will be unable to log in.`)) return;
    try { await adminApi.deactivateUser(u.user_id); toast.success(`Access revoked · ${u.email}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to revoke access'); }
  };

  const activate = async (u) => {
    if (!window.confirm(`Activate ${u.email}? They will regain sign-in access.`)) return;
    try { await adminApi.activateUser(u.user_id); toast.success(`Access activated · ${u.email}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to activate access'); }
  };

  const isLocked = (u) => {
    const t = u.accountLockedUntil;
    return !!t && new Date(t).getTime() > Date.now();
  };

  const unlock = async (u) => {
    try { await adminApi.unlockUser(u.user_id); toast.success(`Sign-in unlocked · ${u.email}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to unlock account'); }
  };

  const cols = [
    { key: 'email', header: 'User', render: u => (
      <div>
        <div className="text-slate-100">{u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}</div>
        <div className="text-[11px] text-slate-500">{u.email}</div>
      </div>
    )},
    { key: 'company', header: 'Company', render: u => <span className="text-slate-400">{u.company || '—'}</span> },
    { key: 'role', header: 'Platform Role', render: u => (
      <Badge tone={u.platformRole === 'PLATFORM_ADMIN' ? 'orange' : 'blue'}>{u.platformRole || 'USER'}</Badge>
    )},
    { key: 'memberships', header: 'Projects', render: u => <span className="text-slate-300 tabular-nums">{(u.projectMemberships || []).length}</span> },
    { key: 'status', header: 'Status', render: u => (
      <div className="flex items-center gap-1.5" data-testid={`status-${u.user_id}`}>
        <Badge tone={u.isActive === false ? 'red' : 'green'}>{u.isActive === false ? 'Inactive' : 'Active'}</Badge>
        {isLocked(u) && <Badge tone="amber">Locked</Badge>}
      </div>
    )},
    { key: 'actions', header: '', render: u => (
      <div className="flex items-center gap-1 justify-end">
        {isLocked(u) && (
          <button onClick={() => unlock(u)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] tracking-wide font-semibold bg-amber-500 hover:bg-amber-600 text-white transition shadow-sm" data-testid={`unlock-${u.user_id}`}><Unlock className="w-3.5 h-3.5"/>Unlock</button>
        )}
        <button onClick={() => setEditing({ ...u })} className={btnGhost} data-testid={`edit-${u.user_id}`}><Edit className="w-3.5 h-3.5"/>Edit</button>
        <button onClick={() => setResetting(u)} className={btnGhost} data-testid={`reset-${u.user_id}`}><KeyRound className="w-3.5 h-3.5"/>Reset</button>
        {u.isActive === false ? (
          <button onClick={() => activate(u)} className={btnSuccess} data-testid={`activate-${u.user_id}`}><UserCheck className="w-3.5 h-3.5"/>Activate</button>
        ) : (
          <button onClick={() => deactivate(u)} className={btnDanger} data-testid={`deact-${u.user_id}`}><UserX className="w-3.5 h-3.5"/>Revoke</button>
        )}
      </div>
    )},
  ];

  return (
    <AdminLayout title="Users" subtitle="Admin · Global Directory">
      <Toolbar>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500"/>
          <input className={`${inpCls} pl-9 w-full`} placeholder="Search by name or email…" value={q} onChange={e => setQ(e.target.value)} data-testid="users-search"/>
        </div>
        <select className={inpCls} value={role} onChange={e => setRole(e.target.value)}>
          <option value="">All roles</option>
          <option value="PLATFORM_ADMIN">Platform Admin</option>
          <option value="USER">User</option>
        </select>
        <select className={inpCls} value={active} onChange={e => setActive(e.target.value)}>
          <option value="">All</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <div className="ml-auto"/>
        <button onClick={() => setShowCreate(true)} className={btnPrimary} data-testid="users-create-btn"><UserPlus className="w-4 h-4"/>Create User</button>
      </Toolbar>
      <Table columns={cols} rows={users} empty={loading ? 'Loading…' : 'No users match your filters.'} getKey={u => u.user_id}/>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#0B1220] border-slate-800 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle>Create New User</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            {[['Email','email','email','rajesh@company.com'],['First Name','firstName','text','Rajesh'],['Last Name','lastName','text','Kumar'],['Company','company','text','Optional'],['Temp Password (≥ 6)','password','password','']].map(([l,k,t,ph]) => (
              <div key={k}><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{l}</label>
                <input type={t} required={['email','firstName','password'].includes(k)} className={`${inpCls} w-full mt-1`} placeholder={ph} value={createForm[k]} onChange={e => setCreateForm(f => ({...f, [k]: e.target.value}))} data-testid={`create-${k}`}/>
              </div>
            ))}
            <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Platform Role</label>
              <select className={`${inpCls} w-full mt-1`} value={createForm.platformRole} onChange={e => setCreateForm(f => ({...f, platformRole: e.target.value}))}>
                {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <button type="submit" className={`${btnPrimary} w-full justify-center`} data-testid="create-submit">Create</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0B1220] border-slate-800 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle>Edit {editing?.email}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              {[['First Name','firstName'],['Last Name','lastName'],['Company','company']].map(([l,k]) => (
                <div key={k}><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{l}</label>
                  <input className={`${inpCls} w-full mt-1`} value={editing[k] || ''} onChange={e => setEditing(p => ({...p, [k]: e.target.value}))}/>
                </div>
              ))}
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Platform Role</label>
                <select className={`${inpCls} w-full mt-1`} value={editing.platformRole || 'USER'} onChange={e => setEditing(p => ({...p, platformRole: e.target.value}))}>
                  {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={editing.isActive !== false} onChange={e => setEditing(p => ({...p, isActive: e.target.checked}))}/>
                Active (can sign in)
              </label>
              <button onClick={saveEdit} className={`${btnPrimary} w-full justify-center`} data-testid="edit-save">Save</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetting} onOpenChange={(o) => !o && (setResetting(null), setPwd(''))}>
        <DialogContent className="bg-[#0B1220] border-slate-800 text-slate-100 max-w-sm">
          <DialogHeader><DialogTitle>Reset Password — {resetting?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">The user will be required to use this new password on next sign-in.</p>
            <input type="password" placeholder="New password (≥ 6 chars)" className={`${inpCls} w-full`} value={pwd} onChange={e => setPwd(e.target.value)} data-testid="reset-input"/>
            <button onClick={reset} className={`${btnPrimary} w-full justify-center`} data-testid="reset-submit">Reset Password</button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
